import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@carrent/ui'
import { kbApi } from '@carrent/api'
import { UserPicker } from '../../tasks/UserPicker'

const LEVELS = [
  { value: 'read', label: 'Чтение', hint: 'Читать опубликованные статьи' },
  { value: 'write', label: 'Правка', hint: 'Предлагать изменения — через проверку' },
  { value: 'admin', label: 'Управление', hint: 'Публиковать, проверять чужие правки, раздавать доступ' },
]

const SUBJECT_TABS = [
  { value: 'group', label: 'Группе' },
  { value: 'role', label: 'Роли' },
  { value: 'user', label: 'Человеку' },
]

const ROLE_LABELS = { user: 'Все водители', manager: 'Все менеджеры', admin: 'Все админы' }

/**
 * Доступ к пространству.
 *
 * Субъект — группа, роль или конкретный человек, и порядок вкладок
 * такой же: доступ по группе — основной способ, роль целиком нужна
 * реже, персональный доступ — исключение. Уровень у субъекта один:
 * повторная выдача меняет его, а не добавляет второй.
 */
export function SpaceSettings({ spaceKey, onClose }) {
  const [grants, setGrants] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([kbApi.grants(spaceKey), kbApi.groups().catch(() => null)])
      .then(([g, gr]) => {
        setGrants(g.data?.data || [])
        // Список групп доступен только глобальному админу; админ
        // пространства без этого права выдаёт доступ по роли и людям.
        setGroups(gr?.data?.data || [])
        setError('')
      })
      .catch((e) => setError(e.response?.data?.message || 'Не удалось загрузить доступы'))
      .finally(() => setLoading(false))
  }, [spaceKey])

  useEffect(() => { load() }, [load])

  const revoke = async (grantId) => {
    try {
      await kbApi.removeGrant(spaceKey, grantId)
      load()
    } catch (e) {
      alert(e.response?.data?.message || 'Не удалось отозвать доступ')
    }
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true"
      aria-label={`Доступ к пространству ${spaceKey}`}>
      <div className="modal modal--wide">
        <header className="modal__head">
          <h2 className="modal__title">Кто видит это пространство</h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="modal__body">
          <GrantForm spaceKey={spaceKey} groups={groups} onAdded={load} />

          {loading && <div className="groups__loading"><div className="spinner" /></div>}
          {error && <p className="field__error">{error}</p>}

          {!loading && grants.length === 0 && (
            <p className="groups__empty">
              Доступ пока ни у кого. Пространство видят только администраторы.
            </p>
          )}

          {grants.length > 0 && (
            <table className="groups__table">
              <thead>
                <tr>
                  <th>Кому</th>
                  <th>Уровень</th>
                  <th aria-label="Действия" />
                </tr>
              </thead>
              <tbody>
                {grants.map((g) => (
                  <tr key={g.id}>
                    <td>
                      <span className="grants__subject">
                        {g.subject_type === 'role'
                          ? ROLE_LABELS[g.subject_id] || g.subject_id
                          : g.subject_name}
                      </span>
                      <span className="grants__kind">{SUBJECT_LABEL[g.subject_type]}</span>
                    </td>
                    <td>{LEVELS.find((l) => l.value === g.level)?.label || g.level}</td>
                    <td className="groups__actions">
                      <button type="button" className="btn btn--ghost btn--sm groups__danger"
                        onClick={() => revoke(g.id)}>
                        Отозвать
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

const SUBJECT_LABEL = { role: 'роль', group: 'группа', user: 'человек' }

function GrantForm({ spaceKey, groups, onAdded }) {
  const [tab, setTab] = useState('group')
  const [level, setLevel] = useState('read')
  const [groupId, setGroupId] = useState('')
  const [role, setRole] = useState('user')
  const [user, setUser] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    const subjectId = tab === 'group' ? groupId : tab === 'role' ? role : user?.id
    if (!subjectId) {
      setError(tab === 'group' ? 'Выберите группу' : 'Выберите человека')
      return
    }
    setSaving(true)
    setError('')
    try {
      await kbApi.addGrant(spaceKey, tab, subjectId, level)
      setUser(null)
      setGroupId('')
      onAdded()
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось выдать доступ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="grants__form">
      <div className="grants__tabs" role="tablist" aria-label="Кому выдать доступ">
        {SUBJECT_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            className={`grants__tab${tab === t.value ? ' is-active' : ''}`}
            onClick={() => { setTab(t.value); setError('') }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grants__row">
        <div className="grants__subject-field">
          {tab === 'group' && (
            groups.length ? (
              <select className="input" value={groupId} aria-label="Группа"
                onChange={(e) => setGroupId(e.target.value)}>
                <option value="">Выберите группу</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}{g.kind === 'region' ? ' · авто' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <p className="grants__note">
                Список групп доступен администратору. Выдайте доступ по роли или человеку.
              </p>
            )
          )}
          {tab === 'role' && (
            <select className="input" value={role} aria-label="Роль"
              onChange={(e) => setRole(e.target.value)}>
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          )}
          {tab === 'user' && (
            <UserPicker selected={user} onChange={setUser} placeholder="Имя или телефон" />
          )}
        </div>

        <select className="input grants__level" value={level} aria-label="Уровень доступа"
          onChange={(e) => setLevel(e.target.value)}>
          {LEVELS.map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>

        <button type="button" className="btn btn--primary" onClick={submit} disabled={saving}>
          Выдать
        </button>
      </div>

      <p className="grants__hint">{LEVELS.find((l) => l.value === level)?.hint}</p>
      {error && <p className="field__error">{error}</p>}
    </div>
  )
}
